import React, { useEffect, useMemo, useState } from 'react';
import { http } from "../lib/axios";
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  FormControlLabel,
  TextField,
  Typography,
} from '@mui/material';
import AdminWorkspaceNav from '../components/AdminWorkspaceNav';
import ContextHelpButton from '../components/ContextHelpButton';

const VENUE_TYPE_OPTIONS = [
  { value: 'home', label: 'Home' },
  { value: 'cafe', label: 'Cafe' },
  { value: 'art_gallery', label: 'Art Gallery' },
  { value: 'library', label: 'Library' },
  { value: 'restaurant', label: 'Restaurant' },
  { value: 'retail_space', label: 'Retail Space' },
  { value: 'other', label: 'Other' },
];

const FOOT_TRAFFIC_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'very_high', label: 'Very High' },
];

const DISPLAY_AVAILABILITY_OPTIONS = [
  { value: '', label: 'Not set' },
  { value: 'temporary', label: 'Temporary' },
  { value: 'rotating', label: 'Rotating' },
  { value: 'ongoing', label: 'Ongoing' },
  { value: 'limited', label: 'Limited' },
];

const ART_PREFERENCE_OPTIONS = [
  'Painting',
  'Sculpture',
  'Photography',
  'Mixed Media',
  'Digital',
  'Installation',
  'Drawing',
  'Print',
];

const glassPanelSx = {
  backgroundColor: 'rgba(255,255,255,0.72)',
  backdropFilter: 'blur(14px)',
  borderRadius: 3,
  boxShadow: '0 12px 30px rgba(0,0,0,0.08)',
};

const sectionWidthSx = {
  width: '100%',
  maxWidth: 1120,
  mx: 'auto',
};

function hostDisplayName(host) {
  const first = host?.user?.first_name || '';
  const last = host?.user?.last_name || '';
  return `${first} ${last}`.trim() || host?.user?.username || 'Unnamed Host';
}

function venueTypeLabel(value) {
  return VENUE_TYPE_OPTIONS.find((option) => option.value === value)?.label || value || 'Not set';
}

function profileCompleteness(host) {
  const checks = [
    host?.venue_type,
    host?.address,
    host?.venue_description,
    host?.display_space_details,
    Array.isArray(host?.art_preferences) && host.art_preferences.length > 0,
    host?.curation_philosophy,
    host?.foot_traffic_estimate,
    host?.artwork_display_availability,
    host?.hosting_motivation,
  ];
  const complete = checks.filter(Boolean).length;
  return Math.round((complete / checks.length) * 100);
}

function missingHostFields(host) {
  const missing = [];
  if (!host?.address) missing.push('Address');
  if (!host?.venue_description) missing.push('Venue description');
  if (!host?.display_space_details) missing.push('Display space details');
  if (!host?.curation_philosophy) missing.push('Curation philosophy');
  if (!host?.foot_traffic_estimate) missing.push('Foot traffic');
  if (!host?.artwork_display_availability) missing.push('Display availability');
  if (!Array.isArray(host?.art_preferences) || host.art_preferences.length === 0) {
    missing.push('Art preferences');
  }
  return missing;
}

function stripeConnectState(host) {
  if (host?.payouts_enabled) return 'ready';
  if (host?.stripe_account_id || host?.details_submitted) return 'action_needed';
  return 'not_connected';
}

function stripeConnectLabel(state) {
  if (state === 'ready') return 'Stripe Ready';
  if (state === 'action_needed') return 'Stripe Action Needed';
  return 'Stripe Not Connected';
}

function stripeConnectColor(state) {
  if (state === 'ready') return 'success';
  if (state === 'action_needed') return 'warning';
  return 'default';
}

export default function HostManager() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [hosts, setHosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [venueFilter, setVenueFilter] = useState('all');
  const [completenessFilter, setCompletenessFilter] = useState('all');
  const [selectedHost, setSelectedHost] = useState(null);
  const [editingHost, setEditingHost] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const hostFocusId = searchParams.get('host') || 'all';

  const loadHosts = async ({ silent = false } = {}) => {
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    setError('');
    try {
      const response = await http.get('/hosts/');
      setHosts(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Unable to load host profiles.');
    } finally {
      if (silent) {
        setRefreshing(false);
      } else {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadHosts();
  }, []);

  const filteredHosts = useMemo(() => {
    const q = search.trim().toLowerCase();
    return hosts.filter((host) => {
      const haystack = [
        hostDisplayName(host),
        host?.user?.username,
        host?.user?.email,
        venueTypeLabel(host?.venue_type),
        host?.address,
        host?.venue_description,
        host?.curation_philosophy,
        host?.hosting_motivation,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      const searchMatch = !q || haystack.includes(q);
      const venueMatch = venueFilter === 'all' || host?.venue_type === venueFilter;
      const completeness = profileCompleteness(host);
      const completenessMatch =
        completenessFilter === 'all' ||
        (completenessFilter === 'attention' && completeness < 70) ||
        (completenessFilter === 'strong' && completeness >= 70);
      const hostFocusMatch = hostFocusId === 'all' || String(host?.id) === String(hostFocusId);

      return searchMatch && venueMatch && completenessMatch && hostFocusMatch;
    });
  }, [hosts, search, venueFilter, completenessFilter, hostFocusId]);

  const summary = useMemo(() => {
    const galleries = hosts.filter((host) => host?.venue_type === 'art_gallery').length;
    const withDisplayPlan = hosts.filter((host) => host?.artwork_display_availability).length;
    const needsAttention = hosts.filter((host) => profileCompleteness(host) < 70).length;
    const payoutsReady = hosts.filter((host) => stripeConnectState(host) === 'ready').length;
    const stripeActionNeeded = hosts.filter((host) => stripeConnectState(host) === 'action_needed').length;
    const withRepresentedArtists = hosts.filter((host) => (host?.represented_artist_count || 0) > 0).length;
    const avgCompleteness = hosts.length
      ? Math.round(hosts.reduce((sum, host) => sum + profileCompleteness(host), 0) / hosts.length)
      : 0;

    return {
      total: hosts.length,
      galleries,
      withDisplayPlan,
      needsAttention,
      payoutsReady,
      stripeActionNeeded,
      withRepresentedArtists,
      avgCompleteness,
    };
  }, [hosts]);

  const openEditDialog = (host) => {
    setEditingHost(host);
    setEditForm({
      venue_type: host?.venue_type || 'cafe',
      address: host?.address || '',
      venue_description: host?.venue_description || '',
      display_space_details: host?.display_space_details || '',
      art_preferences: Array.isArray(host?.art_preferences) ? host.art_preferences : [],
      curation_philosophy: host?.curation_philosophy || '',
      staffing_during_open_hours: Boolean(host?.staffing_during_open_hours),
      foot_traffic_estimate: host?.foot_traffic_estimate || '',
      artwork_display_availability: host?.artwork_display_availability || '',
      hosting_motivation: host?.hosting_motivation || '',
      hosting_outcome_goals: host?.hosting_outcome_goals || '',
    });
  };

  const closeEditDialog = () => {
    if (saving) return;
    setEditingHost(null);
    setEditForm(null);
  };

  const handleEditField = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!editingHost?.id || !editForm) return;
    setSaving(true);
    try {
      await http.patch(`/hosts/${editingHost.id}/`, editForm);
      await loadHosts({ silent: true });
      setSnackbar({ open: true, message: 'Host profile updated.', severity: 'success' });
      closeEditDialog();
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.detail || 'Failed to update the host profile.',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    try {
      await http.delete(`/hosts/${deleteTarget.id}/`);
      setHosts((prev) => prev.filter((host) => host.id !== deleteTarget.id));
      setDeleteTarget(null);
      setSnackbar({ open: true, message: 'Host profile deleted.', severity: 'success' });
    } catch (err) {
      setSnackbar({
        open: true,
        message: err?.response?.data?.detail || 'Failed to delete the host profile.',
        severity: 'error',
      });
    }
  };

  return (
    <Container sx={{ mt: 5, mb: 6 }}>
      <Stack spacing={3}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, mb: 0 }}>
              Host Manager
            </Typography>
            <ContextHelpButton section="admin-help" title="Help for host manager" />
          </Box>
          <Typography variant="body1" color="text.secondary">
            Manage existing host profiles, review venue readiness, and update the host-facing fields that drive discovery and hosting workflows.
          </Typography>
        </Box>

        <Box sx={sectionWidthSx}>
          <AdminWorkspaceNav subtitle="Manage host profiles inside the shared admin workspace, with the same route structure and navigation as the other manager pages." />
        </Box>

        <Grid container spacing={2} sx={sectionWidthSx}>
          <Grid item xs={12} md={3}>
            <Paper sx={{ ...glassPanelSx, p: 2.25 }}>
              <Typography variant="overline" color="text.secondary">Hosts</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{summary.total}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ ...glassPanelSx, p: 2.25 }}>
              <Typography variant="overline" color="text.secondary">Art Galleries</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{summary.galleries}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ ...glassPanelSx, p: 2.25 }}>
              <Typography variant="overline" color="text.secondary">Display Plan Set</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{summary.withDisplayPlan}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ ...glassPanelSx, p: 2.25 }}>
              <Typography variant="overline" color="text.secondary">Needs Attention</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{summary.needsAttention}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ ...glassPanelSx, p: 2.25 }}>
              <Typography variant="overline" color="text.secondary">Payouts Ready</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{summary.payoutsReady}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ ...glassPanelSx, p: 2.25 }}>
              <Typography variant="overline" color="text.secondary">Stripe Action Needed</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{summary.stripeActionNeeded}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ ...glassPanelSx, p: 2.25 }}>
              <Typography variant="overline" color="text.secondary">Hosts With Represented Artists</Typography>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>{summary.withRepresentedArtists}</Typography>
            </Paper>
          </Grid>
        </Grid>

        <Paper sx={{ ...glassPanelSx, ...sectionWidthSx, p: 2.5 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              label="Search hosts"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Name, username, email, venue type, address..."
              fullWidth
            />
            <TextField
              select
              label="Venue type"
              value={venueFilter}
              onChange={(event) => setVenueFilter(event.target.value)}
              sx={{ minWidth: { xs: '100%', md: 220 } }}
            >
              <MenuItem value="all">All venue types</MenuItem>
              {VENUE_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Completeness"
              value={completenessFilter}
              onChange={(event) => setCompletenessFilter(event.target.value)}
              sx={{ minWidth: { xs: '100%', md: 200 } }}
            >
              <MenuItem value="all">All profiles</MenuItem>
              <MenuItem value="attention">Needs attention</MenuItem>
              <MenuItem value="strong">70%+ complete</MenuItem>
            </TextField>
            <Button
              variant="outlined"
              onClick={() => {
                setSearch('');
                setVenueFilter('all');
                setCompletenessFilter('all');
              }}
            >
              Clear Filters
            </Button>
            <Button variant="contained" onClick={() => loadHosts({ silent: true })} disabled={refreshing}>
              {refreshing ? 'Refreshing…' : 'Refresh'}
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Showing {filteredHosts.length} of {hosts.length} host profiles. Average completeness: {summary.avgCompleteness}%.
          </Typography>
        </Paper>

        {error && <Alert severity="error">{error}</Alert>}

        {loading ? (
          <Paper sx={{ ...glassPanelSx, ...sectionWidthSx, p: 3 }}>
            <Typography>Loading host profiles…</Typography>
          </Paper>
        ) : filteredHosts.length === 0 ? (
          <Paper sx={{ ...glassPanelSx, ...sectionWidthSx, p: 3 }}>
            <Typography color="text.secondary">No host profiles match the current filters.</Typography>
          </Paper>
        ) : (
          <Grid container spacing={3} sx={sectionWidthSx}>
            {filteredHosts.map((host) => (
              <Grid item xs={12} key={host.id}>
                <Paper sx={{ ...glassPanelSx, ...sectionWidthSx, p: 2.5 }}>
                  <Stack
                    direction={{ xs: 'column', lg: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems={{ xs: 'stretch', lg: 'stretch' }}
                    sx={{ minHeight: { lg: 220 } }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ xs: 'flex-start', sm: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>
                          {hostDisplayName(host)}
                        </Typography>
                        <Chip size="small" label={`Completeness ${profileCompleteness(host)}%`} />
                        <Chip size="small" variant="outlined" label={venueTypeLabel(host?.venue_type)} />
                        <Chip
                          size="small"
                          color={stripeConnectColor(stripeConnectState(host))}
                          label={stripeConnectLabel(stripeConnectState(host))}
                        />
                        {profileCompleteness(host) < 70 && (
                          <Chip size="small" color="warning" label="Needs attention" />
                        )}
                      </Stack>

                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        @{host?.user?.username} | {host?.user?.email || 'No email'}
                      </Typography>

                      <Typography variant="body2" sx={{ mt: 1 }}>
                        {host?.venue_description || 'No venue description yet.'}
                      </Typography>

                      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
                        <Chip size="small" label={`${host?.hosted_artwork_count || 0} hosted artworks`} />
                        <Chip size="small" label={`${host?.represented_artist_count || 0} represented artists`} />
                        <Chip size="small" label={`${host?.represented_inventory_count || 0} represented inventory`} />
                        <Chip size="small" label={`${host?.sales_count || 0} sales`} />
                      </Stack>

                      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 1.5 }}>
                        {(host?.art_preferences || []).length > 0 ? (
                          host.art_preferences.map((preference) => (
                            <Chip key={`${host.id}-${preference}`} size="small" variant="outlined" label={preference} />
                          ))
                        ) : (
                          <Chip size="small" variant="outlined" label="No art preferences" />
                        )}
                      </Stack>

                      <Grid container spacing={1.5} sx={{ mt: 1 }}>
                        <Grid item xs={12} md={4}>
                          <Typography variant="caption" color="text.secondary">Address</Typography>
                          <Typography variant="body2">{host?.address || 'Not provided'}</Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography variant="caption" color="text.secondary">Display availability</Typography>
                          <Typography variant="body2">{host?.artwork_display_availability || 'Not provided'}</Typography>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography variant="caption" color="text.secondary">Foot traffic</Typography>
                          <Typography variant="body2">{host?.foot_traffic_estimate || 'Not provided'}</Typography>
                        </Grid>
                      </Grid>

                      <Stack
                        direction={{ xs: 'row', sm: 'row' }}
                        spacing={1}
                        flexWrap="wrap"
                        sx={{ mt: 2 }}
                      >
                        <Button variant="outlined" onClick={() => navigate(`/admin/artworks?hosted_host=${host.id}`)}>
                          View Hosted Artworks
                        </Button>
                        {host?.venue_type === 'art_gallery' && (
                          <Button variant="outlined" onClick={() => navigate(`/admin/artworks?gallery_host=${host.id}`)}>
                            View Gallery Inventory
                          </Button>
                        )}
                        <Button variant="outlined" onClick={() => navigate(`/hosts/${host.id}`)}>
                          Open Public Profile
                        </Button>
                        <Button variant="outlined" onClick={() => setSelectedHost(host)}>
                          View Details
                        </Button>
                        <Button variant="outlined" onClick={() => openEditDialog(host)}>
                          Edit
                        </Button>
                        <Button variant="outlined" color="error" onClick={() => setDeleteTarget(host)}>
                          Delete
                        </Button>
                      </Stack>
                    </Box>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        )}
      </Stack>

      <Dialog open={Boolean(selectedHost)} onClose={() => setSelectedHost(null)} fullWidth maxWidth="md">
        <DialogTitle>Host Profile Details</DialogTitle>
        <DialogContent dividers>
          {selectedHost && (
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {hostDisplayName(selectedHost)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  @{selectedHost?.user?.username} | {selectedHost?.user?.email || 'No email'}
                </Typography>
              </Box>

              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Chip label={`Completeness ${profileCompleteness(selectedHost)}%`} />
                <Chip variant="outlined" label={venueTypeLabel(selectedHost?.venue_type)} />
                <Chip
                  color={stripeConnectColor(stripeConnectState(selectedHost))}
                  label={stripeConnectLabel(stripeConnectState(selectedHost))}
                />
                <Chip
                  color={selectedHost?.artwork_display_availability ? 'success' : 'default'}
                  label={selectedHost?.artwork_display_availability ? 'Display plan set' : 'Display plan missing'}
                />
              </Stack>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Venue description</Typography>
                  <Typography variant="body2">{selectedHost?.venue_description || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Address</Typography>
                  <Typography variant="body2">{selectedHost?.address || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Display space details</Typography>
                  <Typography variant="body2">{selectedHost?.display_space_details || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Curation philosophy</Typography>
                  <Typography variant="body2">{selectedHost?.curation_philosophy || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Hosting motivation</Typography>
                  <Typography variant="body2">{selectedHost?.hosting_motivation || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Hosting outcome goals</Typography>
                  <Typography variant="body2">{selectedHost?.hosting_outcome_goals || 'Not provided'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Stripe account</Typography>
                  <Typography variant="body2">{selectedHost?.stripe_account_id || 'Not connected'}</Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="caption" color="text.secondary">Payout readiness</Typography>
                  <Typography variant="body2">{stripeConnectLabel(stripeConnectState(selectedHost))}</Typography>
                </Grid>
              </Grid>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Art Preferences</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  {(selectedHost?.art_preferences || []).length > 0 ? (
                    selectedHost.art_preferences.map((preference) => (
                      <Chip key={`${selectedHost.id}-${preference}`} size="small" variant="outlined" label={preference} />
                    ))
                  ) : (
                    <Chip size="small" variant="outlined" label="No art preferences" />
                  )}
                </Stack>
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Host Inventory Snapshot</Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Chip label={`${selectedHost?.hosted_artwork_count || 0} hosted artworks`} />
                  <Chip label={`${selectedHost?.represented_artist_count || 0} represented artists`} />
                  <Chip label={`${selectedHost?.represented_inventory_count || 0} represented inventory`} />
                  <Chip label={`${selectedHost?.sales_count || 0} sales`} />
                </Stack>
              </Box>

              <Alert severity={missingHostFields(selectedHost).length > 0 ? 'warning' : 'success'}>
                {missingHostFields(selectedHost).length > 0
                  ? `Missing profile details: ${missingHostFields(selectedHost).join(', ')}`
                  : 'This host profile has the core venue fields filled in.'}
              </Alert>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => navigate(`/admin/artworks?hosted_host=${selectedHost?.id || ''}`)} disabled={!selectedHost?.id}>
            View Hosted Artworks
          </Button>
          {selectedHost?.venue_type === 'art_gallery' && (
            <Button onClick={() => navigate(`/admin/artworks?gallery_host=${selectedHost?.id || ''}`)} disabled={!selectedHost?.id}>
              View Gallery Inventory
            </Button>
          )}
          <Button onClick={() => navigate(`/hosts/${selectedHost?.id || ''}`)} disabled={!selectedHost?.id}>
            Open Public Profile
          </Button>
          <Button onClick={() => setSelectedHost(null)}>Close</Button>
          <Button
            variant="contained"
            onClick={() => {
              const target = selectedHost;
              setSelectedHost(null);
              if (target) openEditDialog(target);
            }}
          >
            Edit Profile
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editingHost)} onClose={closeEditDialog} fullWidth maxWidth="md">
        <DialogTitle>Edit Host Profile</DialogTitle>
        <DialogContent dividers>
          {editForm && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="info">
                Account identity fields are read-only here. This dialog updates the live host profile fields exposed by the current backend serializer.
              </Alert>
              <TextField
                select
                label="Venue type"
                value={editForm.venue_type}
                onChange={(event) => handleEditField('venue_type', event.target.value)}
                fullWidth
              >
                {VENUE_TYPE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="Address"
                value={editForm.address}
                onChange={(event) => handleEditField('address', event.target.value)}
                fullWidth
              />
              <TextField
                label="Venue description"
                value={editForm.venue_description}
                onChange={(event) => handleEditField('venue_description', event.target.value)}
                multiline
                minRows={3}
                fullWidth
              />
              <TextField
                label="Display space details"
                value={editForm.display_space_details}
                onChange={(event) => handleEditField('display_space_details', event.target.value)}
                fullWidth
              />
              <TextField
                select
                label="Art preferences"
                SelectProps={{
                  multiple: true,
                  value: editForm.art_preferences,
                  onChange: (event) => handleEditField('art_preferences', event.target.value),
                  renderValue: (selected) => selected.join(', '),
                }}
                fullWidth
              >
                {ART_PREFERENCE_OPTIONS.map((option) => (
                  <MenuItem key={option} value={option}>{option}</MenuItem>
                ))}
              </TextField>
              <TextField
                label="Curation philosophy"
                value={editForm.curation_philosophy}
                onChange={(event) => handleEditField('curation_philosophy', event.target.value)}
                multiline
                minRows={3}
                fullWidth
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={editForm.staffing_during_open_hours}
                    onChange={(event) => handleEditField('staffing_during_open_hours', event.target.checked)}
                  />
                }
                label="Staffing during open hours"
              />
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  select
                  label="Foot traffic estimate"
                  value={editForm.foot_traffic_estimate}
                  onChange={(event) => handleEditField('foot_traffic_estimate', event.target.value)}
                  fullWidth
                >
                  {FOOT_TRAFFIC_OPTIONS.map((option) => (
                    <MenuItem key={option.value || 'none'} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
                <TextField
                  select
                  label="Artwork display availability"
                  value={editForm.artwork_display_availability}
                  onChange={(event) => handleEditField('artwork_display_availability', event.target.value)}
                  fullWidth
                >
                  {DISPLAY_AVAILABILITY_OPTIONS.map((option) => (
                    <MenuItem key={option.value || 'none'} value={option.value}>{option.label}</MenuItem>
                  ))}
                </TextField>
              </Stack>
              <TextField
                label="Hosting motivation"
                value={editForm.hosting_motivation}
                onChange={(event) => handleEditField('hosting_motivation', event.target.value)}
                multiline
                minRows={2}
                fullWidth
              />
              <TextField
                label="Hosting outcome goals"
                value={editForm.hosting_outcome_goals}
                onChange={(event) => handleEditField('hosting_outcome_goals', event.target.value)}
                multiline
                minRows={2}
                fullWidth
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? 'Saving…' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)}>
        <DialogTitle>Delete Host Profile</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Delete the host profile for <strong>{deleteTarget ? hostDisplayName(deleteTarget) : ''}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This is an admin action and may remove live venue data, so it should be used carefully.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}
